import styles from "@/styles/search.module.scss"
import React, { useState, useEffect, useCallback } from 'react'
import DefaultLayout from "@/components/DefaultLayout"
import SliderBarLeft from "@/components/SliderBarLeft"
import SliderBarRight from "@/components/SliderBarRight"
import BreadCrum from '@/components/global/BreadCrum'
import GoodsComponentWithDiscountNew from '@/components/GoodsComponentWithDiscountNew'
import CategoryFilter from "@/components/category/CategoryFilter"
import FilterItem from "@/components/search/FilterItem"
import NewArrivals from "@/components/category/NewArrivals"
import LatestReviews from "@/components/category/LatestReviews"
import { debounce } from 'lodash';

import {
  CaretLeftOutlined,
  CaretRightOutlined,
  CloseOutlined
} from '@ant-design/icons';

import type { PaginationProps } from 'antd';
import { Select, Pagination, Spin } from 'antd';
import categoryApi from '@/service/modules/categoryApi'

import Head from "next/head"
import { getCookie, setCookie } from '@/utils/cookie'
import { useRouter } from 'next/router'
import GoogleADSense from "@/components/GoogleADSense"
import dayjs from 'dayjs'

import getConfig from 'next/config'
import Link from "next/link"
import Script from "next/script"
import { matomoSetCustomDimension } from "@/utils/matomoEvent"
import GoogleOneTap from "@/components/GoogleOneTap"
const { publicRuntimeConfig } = getConfig()
const apiDomain = publicRuntimeConfig.apiDomain

interface ProductItem {
  productID: string;
  name: string;
  url: string;
  imgUrl: string;
  reviewCount: number;
  price: number;
  normalPrice: number;
  freeShipping: number;
  onSale: number;
  reduced: number;
  isNew: number;
  position?: number,
  location?: string;
  isEmailOnly: any;
  inStock?: number;
  isGroup?: number;
  promotionTypeID: string | number;
  promotion: {
    productID: number;
    promotionType: number;
    promotionPrice: number;
    normalPrice: number;
    endDate: string;
    status: number;
    notified: number;
    pointRate: string;
    promotionRate: number;
  };
  review: {
    avgRank: number;
    total: number;
  };
  shortDescription?: string;
  visitor?: {
    weeklyVisitors: number;
  }
  rankNum?: number;
}

interface BreadcrumbItem {
  name: string;
  url: string;
  current: boolean;
}

// 定义分类项接口
interface sideFilterItem {
  name: string;
  count?: number;
  minPrice?: number | null;
  maxPrice?: number | null;
}

interface filterTypeItem {
  title: string;
  key: string;
  currentItemName?: string;
  items: sideFilterItem[]
}

// 分类筛选接口
interface cateFilterItem {
  name: string;
  categoryID: number | string;
  parent: number;
  url: string;
  count: number | string;
  isSelected?: boolean;
  isExpand?: boolean;
  subCategories: cateFilterItem[];
}

interface cateFilterType {
  title: string;
  key: string;
  items: cateFilterItem[]
}

interface hotSearchKeywordItem {
  keywords: string;
  url: string;
}

interface areaItem {
  name: string;
  url: string;
}

interface Props {
  categoryID: number | string;
  keyword: string | number;
  categoryNameFilter: string;
  breadCrumb: BreadcrumbItem[];
  sidebarFilter: filterTypeItem[];
  sidebarCateFilter: cateFilterType[];
  pageList: {
    total: number;
    currentPage: number;
    perPage: number;
    lastPage: number;
    items: ProductItem[];
  };
  hotSearchKeyword: hotSearchKeywordItem[];
}

// 获取分类列表数据
async function getProductList(params: any) {
  try {
    // 清空params中的空值键值对
    const dataObj = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null && value !== '')
    );

    let productData = await categoryApi.getCategoryProduct(dataObj)

    if (
      productData.data 
      && productData.data.pageList 
      && productData.data.pageList.items
      && productData.data.pageList.items.length
    ) {
      productData.data.pageList.items.forEach((item: any) => {
        item.imgUrl = item.filename || item.newThumbnail
      })
    }

    return {
      ...productData.data
    }
  } catch (error) {
    console.log('Next.js Error :', dayjs().format('YY-MM-DD HH:mm:ss'), `  Page Category: ${params.categoryPath} Data acquisition failed:`, JSON.stringify(error))
  }
}

// 在服务器端渲染时调用接口，并返回 props 给页面组件
export async function getServerSideProps(context: any) {
  /**
   * 获取 URL 中的 参数
   * slug是一个数组
   * 不管slug有多少层级，在这里我们都只需要获取第一个参数（分类名）
   */
  const {
    slug,
    filterName,
    brand,
    perPage,
    page,
    sortBy,
    sortDirection,
    maxPrice,
    minPrice,
    area
  } = context.query

  /**
   * 获取url参数，提取出分类的路径部分 categoryPath
   */
  const path = slug[0]
  const regex = /([^?]+)/;

  // 使用正则表达式匹配并提取路径部分
  var match = path.match(regex)
  // 获取匹配到的结果
  let categoryPath = match ? match[1] : slug[0];

  const queryFilter = {
    filterName: filterName || null,
    brand: brand || null,
    perPage: perPage || 48,
    page: page || 1,
    isFreeShpping: 0,
    sortBy: sortBy || 'popularity',
    sortDirection: sortDirection || 'desc',
    maxPrice: maxPrice || null,
    minPrice: minPrice || null,
    area: area || null
  }
  
  const data = await getProductList({categoryPath, ...queryFilter})

  if (data) {
    // 优先判断，是否有replaceUrl
    if (data?.link?.replaceUrl) {
      // 获取URL上的参数str
      var url = context.resolvedUrl || ''
      const urlParams = url.split('?')[1] ? `?${url.split('?')[1]}` : ''

      return {
        redirect: {
          destination: data.link.replaceUrl + urlParams,
          permanent: false,
        }
      }
    } else {
      let categoryOriginalData = data.sidebarFilter && data.sidebarFilter.length ? data.sidebarFilter.filter((item: any) => item.key == 'category') : []
      let categoryFilterData = JSON.parse(JSON.stringify(categoryOriginalData))
      if (categoryOriginalData.length) {
        categoryFilterData[0].items.forEach((cateFirstLevel: cateFilterItem) => {
          cateFirstLevel.isSelected = !!(cateFirstLevel.categoryID == data.categoryID)
          cateFirstLevel.url = `${apiDomain}${cateFirstLevel.url}`
  
          cateFirstLevel.subCategories.forEach((cateSecondLevel: cateFilterItem) => {
            cateSecondLevel.isSelected = !!(cateSecondLevel.categoryID == data.categoryID)
            cateSecondLevel.url = `${apiDomain}${cateSecondLevel.url}`
  
            /**
             * 如果有选中，则把三级分类打开
             */
            if (cateSecondLevel.isSelected) {
              cateSecondLevel.isExpand = true
            }
  
            /**
             * 看subCategories有没有选中的分类
             * 如果有，就把二级分类展开（isExpand设置为true）
             */
            let subHasSelected = false
  
            cateSecondLevel.subCategories.forEach((cateThirdLevel: cateFilterItem) => {
              cateThirdLevel.isSelected = !!(cateThirdLevel.categoryID == data.categoryID)
              cateThirdLevel.url = `${apiDomain}${cateThirdLevel.url}`
              if (cateThirdLevel.isSelected) subHasSelected = true
            })
  
            if (subHasSelected) cateSecondLevel.isExpand = true
          })
        })
      }

      let filtersData = data.sidebarFilter.filter((item: any) => item.key != 'category' && item.items.length)
      

      /**
       * 前端把sort-by和price-range的位置换一下，sort-by在前面
       */
      // 找到 sort-by 元素的索引
      const sortByIndex = filtersData.findIndex((item: any) => item.key === 'sort-by');
      if (sortByIndex !== -1) {
        // 找到 price-range 元素的索引
        const priceRangeIndex = filtersData.findIndex((item: any) => item.key === 'price-range');

        // 如果存在 price-range 元素，移除并插入到 sort-by 前面
        if (priceRangeIndex !== -1) {
          const priceRangeElement = filtersData[priceRangeIndex];
          // 从原数组中移除 price-range 元素
          filtersData.splice(priceRangeIndex, 1);
          // 将 price-range 插入到 sort-by 的前一位
          filtersData.splice(sortByIndex, 0, priceRangeElement);
        }
      }
      
      // 默认选择Best Selling
      filtersData.forEach((item: filterTypeItem) => {
        if (item.key == 'sort-by') {
          item.currentItemName = 'Bestselling'
        }
      });

      return {
        props: {
          categoryID: data.categoryID,
          keyword: categoryPath.replace(/\+/g, " "),
          breadCrumb: data.breadCrumb || [],
          pageList: data.pageList || {},
          sidebarFilter: filtersData || [],
          sidebarCateFilter: categoryFilterData || [],

          hotSearchKeyword: data.hotSearchKeyword || [],
        }
      }
    }
  } else {
    // 如果没有返回数据，返回一个 notFound 的标志，让 Next.js 渲染一个内置的 404 页面
    return {
      notFound: true,
    }
  }
}

export default function Category(props: Props) {
  const router = useRouter()
  const { Option } = Select

  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [updating, setUpdating] = useState(false);
  const [filterStr, setFilterStr] = useState('');
  const [filterDivs, setFilterDivs] = useState<React.ReactElement[]>([]);
  const [page, setPage] = useState(props.pageList.currentPage);
  const [perPage, setPerPage] = useState(props.pageList.perPage);
  const [lastPage, setLastPage] = useState(props.pageList.lastPage);
  const [total, setTotal] = useState(props.pageList.total);
  const [pageProductData, setPageProductData] = useState<ProductItem[]>(props.pageList.items);

  const [breadCrumb, setBreadCrumb] = useState<BreadcrumbItem[]>(props.breadCrumb);

  const [sidebarFilter, setSidebarFilter] = useState<filterTypeItem[]>(props.sidebarFilter);
  const [sidebarCateFilter, setSidebarCateFilter] = useState<cateFilterType[]>(props.sidebarCateFilter);
  const [sidebarFilterQuery, setSidebarFilterQuery] = useState<any[]>([]);

  const [popularKeywords, setPopularKeywords] = useState('');
  const [hotSearchKeyword, setHotSearchKeyword] = useState<hotSearchKeywordItem[]>(props.hotSearchKeyword);
  const [shopArea, setShopArea] = useState<areaItem[]>([]);
  const [areaSuggestion, setAreaSuggestion] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [aboutCategory, setAboutCategory] = useState('');

  let pageSizeList = [
    { 
      id: 1,
      name: '48',
      value: 48,
      url: ''
    },
    { 
      id: 2,
      name: '100',
      value: 100,
      url: ''
    },
    { 
      id: 3,
      name: '200',
      value: 200,
      url: ''
    }
  ]

  // 获取分类页面extra数据
  async function getCategoryExtraData() {
    try {
      let resData = await categoryApi.getCategoryExtra(props.categoryID)

      if (resData.data && resData.data) {
        setPopularKeywords(resData.data.popularKeywords)
        setHotSearchKeyword(resData.data.hotSearchKeyword)
        setAreaSuggestion(resData.data.areaSuggestion)
        setCategoryName(resData.data?.category.name || '')
        setAboutCategory(resData.data?.category.description || '')

        // area 数据处理
        if (resData.data.area && resData.data.area.length) {
          let areaList = resData.data.area.map((item: string) => {
            // 调整为首字母大写
            return {
              name: item.charAt(0).toUpperCase()+ item.slice(1),
              url: `/${props.keyword}?area=${item}`
            }
          })
          setShopArea(areaList)
        }
      }
    } catch (error) {
      console.log(`csotd数据获取失败： `, error)
    }
  }

  useEffect(() => {
    // 获取分类页面extra数据
    getCategoryExtraData()
  }, [])

  // 判断是否有aid 或 recoveraid在url，有就将其
  useEffect(() => {
    const { aid, recoveraid }: any = router.query
    let lastAidValue = ''
    let lastRecoveraidValue = ''

    // 检查是否有"aid"参数，如有多个取最后
    if (Array.isArray(aid) && aid.length > 0) {
      lastAidValue = aid[aid.length - 1]
    } else if (typeof aid === 'string') {
      // 如果只有一个"aid"参数，它将是一个字符串
      lastAidValue = aid
    }

    // 检查是否有"recoveraid"参数，如有多个取最后
    if (Array.isArray(recoveraid) && recoveraid.length > 0) {
      lastRecoveraidValue = recoveraid[recoveraid.length - 1]
    } else if (typeof recoveraid === 'string') {
      // 如果只有一个"recoveraid"参数，它将是一个字符串
      lastRecoveraidValue = recoveraid
    }

    // 优先于异步请求，将aid存入cookie, 60秒过期（后续会发起请求重新设置）
    if (lastAidValue || lastRecoveraidValue) {
      let currentDomain = window.location.hostname;
      if(currentDomain == 'www.crazysales.com.au') {
        currentDomain = ".crazysales.com.au"
      }
      setCookie('affiliateid', lastAidValue || lastRecoveraidValue, { expires: 30, secure: true, domain: `${currentDomain}`, })
    }
  }, [router.query])

  // 发现本地有存储aid，重新请求数据
  const getProductDetailWithPromotion = async () => {
    if (!getCookie('affiliateid')) return 

    // 获取分类页面extra数据
    getCategoryExtraData()
  }

  // cookie有留存aid参数，则更新Email价格
  useEffect(() => {
    setTimeout(() => {
      let affiliateid = getCookie('affiliateid')
      if (affiliateid) getProductDetailWithPromotion()
    }, 400)
  }, [])

  // 分页器change事件
  const onChange: PaginationProps['onChange'] = (current) => {
    setPage(current)
    setUpdating(true)
    window.scrollTo({ top: 0 });
  };

  // 切换pageSize
  const handleChangePageSize = (pageSize: number) => {
    setPageSize(pageSize)
  };

  interface suburbOptionsItem {
    id: string;
    name: string
  }

  interface sortByFilterType{
    sortBy: string;
    sortDirection: string;
  }
  const [suburbOptions, setSuburbOptions] = useState<suburbOptionsItem[]>([]);
  const [sortBy, setSortBy] = useState<string | null | undefined>(null);
  const [isFreeShipping, setIsFreeShipping] = useState(false);
  const [sortByFilter, setSortByFilter] = useState<sortByFilterType>({ sortBy: '', sortDirection: '' });
  const [pageSize, setPageSize] = useState(48)

  // 设置页面选中的参数
  useEffect(() => {
    setSuburbOptions(
      [
        { id: '1', name: 'Best Match' },
        { id: '2', name: 'Best Selling' },
        { id: '3', name: 'Price(High to Low)' },
        { id: '4', name: 'Price(Low to High)' },
        { id: '5', name: 'New Arrivals' }
      ]
    )

    setSortBy('2')
  }, [])

  // 切换free shipping按钮
  const handleToggleFreeShipping = () => {
    if (isFreeShipping) {
      setIsFreeShipping(false)

      let filterData = JSON.parse(JSON.stringify(sidebarFilter))
      filterData.forEach((item: filterTypeItem) => {
        if (item.key == 'filtered-by') {
          item.currentItemName = ''
        }
      });

      setSidebarFilter(filterData)
    } else {
      setIsFreeShipping(true)

      // 适配左侧Filter的筛选器
      let filterData = JSON.parse(JSON.stringify(sidebarFilter))
      filterData.forEach((item: filterTypeItem) => {
        if (item.key == 'filtered-by') {
          item.currentItemName = 'Free Shipping'
        }
      });

      setSidebarFilter(filterData)
    }
  }

  // Sort by选择器触发事件
  const handleChangeSort = (value: string) => {
    // 匹配 Filter by 参数
    switch (String(value)) {
      case '1':
        setSortByFilter({ sortBy: 'bestMatch', sortDirection: 'desc' })
        break
      case '2':
        setSortByFilter({ sortBy: 'popularity', sortDirection: 'desc' })
        break
      case '3':
        setSortByFilter({ sortBy: 'price', sortDirection: 'desc' })
        break
      case '4':
        setSortByFilter({ sortBy: 'price', sortDirection: 'asc' })
        break
      case '5':
        setSortByFilter({ sortBy: 'addTime', sortDirection: 'desc' })
        break
      default:
    }
    setSortBy(value)

    // 适配左侧Filter的筛选器
    let filterData = JSON.parse(JSON.stringify(sidebarFilter))
    filterData.forEach((item: filterTypeItem) => {
      if (item.key == 'sort-by') {
        switch (value) {
          case '2':
            item.currentItemName = 'Bestselling'
            break;
          case '3':
            item.currentItemName = 'PriceHigh to Low'
            break;
          case '4':
            item.currentItemName = 'PriceLow to High'
            break;
          case '5':
            item.currentItemName = 'New Arrivals'
            break;
          default:
            item.currentItemName = ''
            break;
        }
      }
    });

    setSidebarFilter(filterData)
  };

  // 切换上一页/下一页
  const handleTurnPage = (turnPage: number) => {
    // turnPage 小于1  或者  大于最后一页的页码，都return掉
    if (turnPage < 1 || turnPage > lastPage) return

    setPage(turnPage)
  }

  // 更新页面数据
  const handleUpdateData = async (params: any, sidebarFilterQuery: any[]) => {
    try {
      if (updating) return
      setUpdating(true)

      const data = await getProductList({categoryPath: props.keyword, ...params})

      // 结束loading，给100ms的延迟，防止页面抖动
      setTimeout(() => {
        setUpdating(false)
      }, 100)
  
      if (data) {
        if (data.link && data.link.replaceUrl) {
          window.location.href = data.link.replaceUrl
        }
        setLastPage(data.pageList.lastPage)
        setPerPage(data.pageList.perPage)
        setTotal(data.pageList.total)
        setPageProductData(data.pageList.items)
  
        setBreadCrumb(data.breadCrumb)
        setHotSearchKeyword(data.hotSearchKeyword)

        let filtersData = data.sidebarFilter.filter((item: any) => item.key != 'category')
        let cateData = data.sidebarFilter.filter((item: any) => item.key == 'category')

        /**
         * 前端把sort-by和price-range的位置换一下，sort-by在前面
         */
        // 找到 sort-by 元素的索引
        const sortByIndex = filtersData.findIndex((item: any) => item.key === 'sort-by');
        if (sortByIndex !== -1) {
          // 找到 price-range 元素的索引
          const priceRangeIndex = filtersData.findIndex((item: any) => item.key === 'price-range');

          // 如果存在 price-range 元素，移除并插入到 sort-by 前面
          if (priceRangeIndex !== -1) {
            const priceRangeElement = filtersData[priceRangeIndex];
            // 从原数组中移除 price-range 元素
            filtersData.splice(priceRangeIndex, 1);
            // 将 price-range 插入到 sort-by 的前一位
            filtersData.splice(sortByIndex, 0, priceRangeElement);
          }
        }

        filtersData.forEach((item: any) => {
          let target = sidebarFilterQuery.filter((targetItem: any) => targetItem.key == item.key)

          if (target.length > 0) {
            item.currentItemName = target[0].currentItemName || ''
          }
          if ((!item.items || item.items.length <= 0) &&  target[0].currentItem.name) {
            item.items.push(target[0].currentItem)
          }
        });

        let filtersDataResult = filtersData.filter((item: any) => item.items.length > 0)
        setSidebarFilter(filtersDataResult || [])

        // 新的sidebar数据
        if (!cateData[0].items || cateData[0].items.length <= 0) {
          cateData = []
          setSidebarCateFilter(cateData)
        } else if (!params.categoryID) {
          setSidebarCateFilter(cateData)
        }
      }
    } catch (error) {
      setUpdating(false)
    }
  }

  // sidebar filter 点击事件
  const handleFilterItemSelect = (filterKey: string, itemName: string) => {
    let filterData = JSON.parse(JSON.stringify(sidebarFilter))

    switch (filterKey) {
      case 'filtered-by':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'filtered-by') {
            if (item.currentItemName != itemName) {
              item.currentItemName = itemName

              setIsFreeShipping(true)
            } else {
              item.currentItemName = ''
              setIsFreeShipping(false)
            }
          }
        });
        break
      case 'hot-brand':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'hot-brand') {
            if (item.currentItemName != itemName) {
              item.currentItemName = itemName
            } else {
              item.currentItemName = ''
            }
          }
        });
        break
      case 'sort-by':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'sort-by') {
            if (item.currentItemName != itemName) {
              item.currentItemName = itemName
              
              switch (itemName) {
                case 'Bestselling':
                  handleChangeSort('2')
                  break;
                case 'PriceHigh to Low':
                  handleChangeSort('3')
                  break;
                case 'PriceLow to High':
                  handleChangeSort('4')
                  break;
                case 'New Arrivals':
                  handleChangeSort('5')
                  break;
                default:
                  break;
              }
            } else {
              item.currentItemName = 'Bestselling'
              handleChangeSort('2')
            }
          }
        });
        break
      case 'price-range':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'price-range') {
            if (item.currentItemName != itemName) {
              item.currentItemName = itemName
            } else {
              item.currentItemName = ''
            }
          }
        });
        break
      default:
        // 默认，把参数剔除
        filterData.forEach((item: filterTypeItem) => {
          item.currentItemName = ''
        });
        break
    }

    setPage(1)
    setSidebarFilter(filterData)
  };

  // 处理请求参数
  const handleQueryParams = (
    page: number, 
    pageSize: number, 
    sortByFilter: sortByFilterType,
    sidebarFilterQuery: any[],
    isFreeShipping: boolean
  ) => {
    let dataObj = { 
      categoryPath: props.keyword,
      page,
      perPage: pageSize,
      brand: '',
      minPrice: '',
      maxPrice: '',
      freeShipping: isFreeShipping ? 1 : 0,
      ...sortByFilter
    }

    if (!dataObj.sortBy || !dataObj.sortDirection) {
      dataObj.sortBy = 'popularity'
      dataObj.sortDirection = 'desc'
    }

    let filterData = JSON.parse(JSON.stringify(sidebarFilterQuery))

    filterData.forEach((item: any) => {
      if (item.currentItemName) {
        switch (item.key) {
          case 'hot-brand':
            dataObj.brand = item.currentItemName
            break
          case 'sort-by':
            // sort-by不做请求参数处理，跟sortByFilter功能重叠了
            break
          case 'price-range': {
            if (item.currentItem) {
              if (item.currentItem.minPrice) dataObj.minPrice = String(item.currentItem.minPrice)
              if (item.currentItem.maxPrice) dataObj.maxPrice = String(item.currentItem.maxPrice)
            }
            break
          }
          default:
            break
        }
      }
    });

    // 剔除无用的参数
    let paramsObj = Object.fromEntries(
      Object.entries(dataObj).filter(([key, value]) => value !== null && value !== '')
    )

    return paramsObj
  }

  // 定义一个去抖函数，延迟300ms执行
  const debouncedFetchProducts = useCallback(
    debounce(async (page, pageSize, sortByFilter, sidebarFilterQuery, isFreeShipping) => {
      try {
        let params = handleQueryParams(page, pageSize, sortByFilter, sidebarFilterQuery, isFreeShipping)
        handleUpdateData(params, sidebarFilterQuery)
      } catch (error) {
        console.error('Failed to fetch products', error);
      }
    }, 300),
    [] // 如果筛选条件和分页状态是不会变的，可以把它们包含到依赖数组中。否则保持数组为空。
  );

  // 当筛选条件或分页状态变化时执行去抖后的数据获取函数
  useEffect(() => {
    if (isFirstLoad) {
      setTimeout(() => {
        setIsFirstLoad(false)
      }, 300);
    } else {
      debouncedFetchProducts(page, pageSize, sortByFilter, sidebarFilterQuery, isFreeShipping);
    }
  }, [page, pageSize, sortByFilter, sidebarFilterQuery, isFreeShipping]);

  // filter 取消事件
  const handleCancelFilter = (filterType: string) => {
    // 直接使用数组映射保持不可变性
    const newFilterData = sidebarFilter.map(item => {
      // 合并判断逻辑：匹配指定类型或处理默认情况
      const isTarget = !!(item.title === filterType)

      return isTarget 
        ? { ...item, currentItemName: '' }
        : item;
    });
  
    setPage(1);
    setSidebarFilter(newFilterData);
  };

  // 当筛选条件变化时输出正确的筛选text
  useEffect(() => {
    let str = ''
    const divs: React.ReactElement[] = [];
    
    let target = sidebarFilter.filter((item: filterTypeItem) => item.currentItemName)
    if (target.length) {
      target.forEach((item: filterTypeItem, index: number) => {
        if (str || index >= 1) {
          str = `${str} > ${item.title}:${item.currentItemName}`
        } else {
          str = ` In ${item.title}:${item.currentItemName}`
        }

        if (item.title !== 'Filtered by') {
          divs.push(
            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
              {index > 0 ? ' > ' : ' In '}
              <div className={styles['filter-divs']}>
                {item.title}: {item.currentItemName}
                <div 
                  className={styles['cancel-btn']} 
                  onClick={() => handleCancelFilter(item.title)}
                >
                  <CloseOutlined style={{ color: '#FFFFFF' }} />
                </div>
              </div>
            </div>
          );
        }
      })
    }

    if (str != filterStr) {
      setFilterStr(str)
    }
    setFilterDivs(divs);
  }, [sidebarFilter]);

  // 当筛选条件变化时输出正确的筛选text
  useEffect(() => {
    let sidebarFilterCopy: any[] = []

    sidebarFilter.forEach((item: filterTypeItem) => {
      let sidebarFilterItem = {
        key: item.key,
        title: item.title,
        currentItemName: item.currentItemName || '',
        currentItem: {}
      }
      let target = item.items.filter((v: sideFilterItem) => v.name == item.currentItemName)
      if (target.length) {
        sidebarFilterItem.currentItem = target[0]
      }
      sidebarFilterCopy.push(sidebarFilterItem)
    })

    if (JSON.stringify(sidebarFilterCopy) != JSON.stringify(sidebarFilterQuery)) {
      setSidebarFilterQuery(sidebarFilterCopy)
    }
  }, [filterStr]);

   useEffect(() => {
    const url = props.breadCrumb.length ? props.breadCrumb[props.breadCrumb.length - 1].url : ''
    if (url) {
      matomoSetCustomDimension(14, url)
    }
  }, [])

  /**
   * emarsys tracking数据推送
   */
  // useEffect(() => {
  //   const subdomain = document.location.protocol === 'https:' ? 'https://recommender' : 'http://cdn';
  //   const id = 'scarab-js-api';

  //   if (document.getElementById(id)) return;

  //   const js = document.createElement('script');
  //   js.id = id;
  //   js.src = `${subdomain}.scarabresearch.com/js/1CBC47CFE1C81625/scarab-v2.js`;

  //   js.onload = () => {

  //       const script = document.createElement('script');
  //       script.type = 'text/javascript';
  //       script.innerHTML = `
  //           var ScarabQueue = ScarabQueue || [];
  //           if (localStorage.getItem('userEmail')) {
  //             ScarabQueue.push(['setEmail', localStorage.getItem('userEmail')]);
  //           }

  //           var categoryPath = Array.isArray(props.breadCrumb)
  //             ? props.breadCrumb
  //                 .map(item => item && item.name ? item.name : '')
  //                 .filter(name => name) // 过滤掉可能的空字符串
  //                 .join(' > ')
  //             : '';
  //             ScarabQueue.push(['category', '${
  //               Array.isArray(props.breadCrumb)
  //                 ? props.breadCrumb
  //                     .map(item => (item && item.name ? item.name : ''))
  //                     .filter(name => name)
  //                     .join(' > ')
  //                 : ''
  //             }']);
  //           ScarabQueue.push(['go']);
  //       `;

  //       document.body.appendChild(script);
  //   };

  //   const fs = document.getElementsByTagName('script')[0];
  //   fs?.parentNode?.insertBefore(js, fs);

  //   return () => {
  //       js.remove();
  //   };
  // }, []);
  
  const itemListElement = props.breadCrumb.map((item: any, number: number) => {
    return {
      "@type": "ListItem",
      "position": number + 2,
      "name": item.name,
      "item": item.url
    }
  })
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.bestdeals.co.nz/"
      },
      ...itemListElement
    ]
  };
  return (
    <>
      <Head>
        <title>Online Shopping in New Zealand: Discount Store - BestDeals.co.nz</title>
        <meta name="keywords" content={``}></meta>
        <meta name="description" content={`Offers the best online shopping in New Zealand with cheap deals on home & garden, appliances, pet supplies, fitness equipment & much more online. Check out our daily sales now!`}></meta>
      </Head>

      <DefaultLayout >
        <div className={`${styles['page-search']} slider-wapper`}>
          <SliderBarLeft></SliderBarLeft>
          <SliderBarRight></SliderBarRight>

          {/* 面包屑 */}
          {
            !!(breadCrumb && breadCrumb.length) &&
            <BreadCrum BreadList={breadCrumb}></BreadCrum>
          }
          <div className={styles['page-container']}>
            <div className={styles['filter-box']}>
              <Spin spinning={updating}>
                {/* 筛选 */}
                {
                  !!(sidebarFilter && sidebarFilter.length) &&
                  <div className={styles['filter-list']}>
                    {
                      sidebarFilter.map((item: filterTypeItem, index: number) => (
                        <div 
                          className={`${styles['filter-item']} ${!sidebarCateFilter || sidebarCateFilter.length <= 0 ? styles['no-last-border'] : ''}`} 
                          key={item.key}
                        >
                          <FilterItem 
                            showCount={true}
                            filterTypeItem={item}
                            onItemSelect={handleFilterItemSelect}
                          ></FilterItem>
                        </div>
                      ))
                    }
                  </div>
                }

                {/* 分类的筛选 */}
                {
                  !!(sidebarCateFilter && sidebarCateFilter.length) &&
                  <div className={styles['category-filter']}>
                    <div className={styles['category-filter-content']}>
                      <CategoryFilter 
                        cateFilterData={sidebarCateFilter}
                      ></CategoryFilter>
                    </div>
                  </div>
                }
              </Spin>

              {/* 新品上市或者热门商品推荐 */}
              {
                !!(props.categoryID) && 
                <div className={styles['new-arrivals']}>
                  <NewArrivals 
                    type={'hotProducts'}
                    category={props.categoryID}
                    title={`New Arrivals & Hot`}
                  ></NewArrivals>
                </div>
              }

              {/* 广告 */}
              <div style={{ marginTop: '20px' }}>
                {/* <GoogleADSense /> */}
              </div>
            </div>

            <div className={styles['container-box']}>
              <Spin spinning={updating}>
                <div className={styles['top-filter-box']}>
                  <div className={styles['top-jump']}>
                    <div className={styles['result-and-filters']}>
                      <div className={`${styles['high-light']} margin-right-5`}>{total}</div>
                      Results for
                      <div className={`${styles['high-light']} margin-left-5 margin-right-5`}>{props.keyword}</div>
                      {filterDivs.length > 0 && (
                        <div className={styles['filters-box']}>
                          {filterDivs}
                        </div>
                      )}
                    </div>

                    <div className={styles['top-jump-box']}>
                      <div><span className={`${styles['high-light']}`}>{page}</span> / {lastPage}</div>
                      {/* 上一页 */}
                      <div 
                        className={`
                          ${styles['top-jump-item']} 
                          ${page <= 1 ? styles['disabled'] : ''}
                        `}
                        onClick={() => { handleTurnPage(page - 1) }}
                      >
                        <CaretLeftOutlined style={ page <= 1 ? { color: '#dddddd' } : undefined }/>
                      </div>

                      {/* 下一页 */}
                      <div 
                        className={`
                          ${styles['top-jump-item']} 
                          ${page >= lastPage ? styles['disabled'] : ''}
                        `}
                        onClick={() => { handleTurnPage(page + 1) }}
                      >
                        <CaretRightOutlined />
                      </div>
                    </div>
                  </div>

                  <div className={styles['page-size-filter']}>
                    <div className={styles['page-size']}>
                      Show:&nbsp;&nbsp;
                      {
                        pageSizeList.map((item: any) => (
                          <div 
                            className={`
                              ${styles['page-size-item']}
                              ${pageSize == item.value ? styles['active'] : ''}
                            `}
                            onClick={() => handleChangePageSize(item.value)}
                            key={item.id}
                          >{item.name}</div>
                        ))
                      }
                    </div>

                    <div className={styles['select-filter']}>
                      <div className={styles['select-filter-item']}>
                        Filtered by:&nbsp;

                        {
                          isFreeShipping
                          ?
                          <div className={styles['cancel-free-shipping']} onClick={handleToggleFreeShipping}>
                            Free Shipping
                            <div className={styles['cancel-btn']}>
                              <CloseOutlined style={{color: '#FFFFFF'}} />
                            </div>
                          </div>
                          :
                          <div className={styles['select-free-shipping']} onClick={handleToggleFreeShipping}>
                            Free Shipping
                          </div>
                        }
                      </div>

                      <div className={styles['select-filter-item']}>
                        Sort by:&nbsp;

                        {
                          !!(sortBy) &&
                          <Select 
                            onChange={handleChangeSort} 
                            size={'small'}
                            style={{ width: '180px' }}
                            className={styles['sort-by-select']}
                            defaultValue={sortBy}
                            value={sortBy}
                          >
                            {
                              !!(suburbOptions && suburbOptions.length) &&

                              suburbOptions.map((item: suburbOptionsItem) => (
                                <Option 
                                  value={item.id}
                                  key={item.id}
                                >
                                  {item.name}
                                </Option>
                              ))
                            }
                          </Select>
                        }
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles['product-list']}>
                  {
                    pageProductData && pageProductData.length
                    ?
                    pageProductData.map((product: ProductItem) => (
                      <div className={styles['product-item']} key={product.productID}>
                        <GoodsComponentWithDiscountNew {...{ ...product, productName: product.name, imgUrl: product.imgUrl }} />
                      </div>
                    ))
                    :
                    <div className={styles['product-empty']}>Sorry, no products available with the selected options.</div>
                  }
                </div>

                {/* 分页器 */}
                {
                  !!(total && lastPage > 1) &&
                  <div className={styles['pagination-box']}>
                    <Pagination
                      key={perPage * total}
                      showQuickJumper
                      showSizeChanger={false}
                      current={page}
                      onChange={onChange}
                      defaultPageSize={perPage}
                      total={total}
                    />
                  </div>
                }
              </Spin>
              
              {
                !!(props.categoryID) && 
                <LatestReviews categoryId={props.categoryID}></LatestReviews>
              }
              
              {
                !!(hotSearchKeyword && hotSearchKeyword.length) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']}>
                    Hot Searches:&nbsp;
                    {
                      hotSearchKeyword.map((item: hotSearchKeywordItem, index: number) => (
                        <span key={item.url}>
                          <Link
                            href={item.url}
                            legacyBehavior
                          >
                            <a rel="noopener noreferrer">
                              {item.keywords}
                            </a>
                          </Link>
                          {
                            !!(index + 1 < hotSearchKeyword.length) &&
                            <span>&nbsp;,&nbsp;</span>
                          }
                        </span>
                      ))
                    }
                  </div>
                </div>
              }

              {
                !!(popularKeywords) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']} dangerouslySetInnerHTML={{ __html: `Popular Category: ${popularKeywords}` }}></div>
                </div>
              }
              
              {
                !!(shopArea && shopArea.length) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']}>
                    Shop By City:&nbsp;
                    {
                      shopArea.map((item: areaItem, index: number) => (
                        <span key={index}>
                          <Link
                            href={item.url}
                            legacyBehavior
                          >
                            <a rel="noopener noreferrer">
                              {item.name}
                            </a>
                          </Link>
                          {
                            !!(index + 1 < shopArea.length) &&
                            <span>&nbsp;,&nbsp;</span>
                          }
                        </span>
                      ))
                    }
                  </div>
                </div>
              }
              
              {
                !!(areaSuggestion) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']} dangerouslySetInnerHTML={{ __html: `${areaSuggestion}` }}></div>
                </div>
              }
              
              {
                !!(aboutCategory) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['header']}>
                    About&nbsp;{categoryName}
                  </div>

                  <div className={styles['content']} dangerouslySetInnerHTML={{ __html: aboutCategory }}></div>
                </div>
              }
              
              <div style={{ marginTop: '20px' }}>
                <GoogleADSense />
              </div>
            </div>
          </div>
          <GoogleOneTap />
        </div>
      </DefaultLayout>

      <Script
        type="application/ld+json"
        id="breadcrumb-schema"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  )
}
